/** @jsx jsx */
import { Box, Flex, Heading, Inline, Paragraph, Stack, Tag } from "@eworkit/iris";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { jsx } from "theme-ui";

import { ResponsiveCard } from "@/components/ResponsiveCard";
import { JobRequestListDto } from "@/data/job-requests/types/JobRequest";
import { formatMultipleLocationsWithCounter } from "@/data/locations";
import { useFormats } from "@/hooks/Formats";
import { usePreviousRoutePath } from "@/infrastructure/Route";
import { router } from "@/views/router";

import { JobRequestClientAvatar } from "../JobRequestClientAvatar";

interface JobsWidgetProps {
  header: string;
  jobs?: Array<Partial<JobRequestListDto>>;
  isPublic?: boolean;
}

export const JobsWidget: FC<JobsWidgetProps> = ({ header, jobs, isPublic, children }) => {
  const { t } = useTranslation(["public", "session"]);
  const { currentLocationState } = usePreviousRoutePath();
  const { formatRate } = useFormats();

  const jobRequestPath = isPublic ? "public-job-requests.detail" : "job-requests.detail";

  if (!jobs || !jobs.length) {
    return null;
  }

  return (
    <ResponsiveCard>
      <Stack gap="xs">
        <Heading size="h-sm">{header}</Heading>

        <Stack>
          {jobs.map(jr => (
            <Box
              key={jr.id}
              as={Link}
              to={{
                pathname: router.get(jobRequestPath, { jobRequestId: jr.id }).path,
                state: currentLocationState,
              }}
              p="xs"
              sx={{
                borderRadius: "8px",
                mx: "-8px",
                color: "unset",
                textDecoration: "none",

                "&:hover": {
                  cursor: "pointer",
                  backgroundColor: "n25",
                },
              }}>
              <Inline gap="xs">
                <JobRequestClientAvatar size="sm" jobRequest={jr} />

                <Stack gap="xxs">
                  <Stack>
                    <Heading size="h-xs">{jr.title}</Heading>
                    <Paragraph size="p-md">{jr.client?.name ?? jr.origin?.name}</Paragraph>
                    <Paragraph size="p-md">{formatMultipleLocationsWithCounter(t, jr.locations)}</Paragraph>
                  </Stack>

                  <Inline gap="xxs">
                    {jr.remoteness === 100 && <Tag>{t("session:JOB_REQUESTS.DETAIL.REMOTENESS.REMOTE")}</Tag>}
                    {jr.rate && !!jr.rate.maxRate && (
                      <Tag>
                        {formatRate(
                          jr.rate.maxRate,
                          jr.rate.currency,
                          jr.rate.clientRateType === "HOURLY" ? "h" : undefined,
                        )}
                      </Tag>
                    )}
                  </Inline>
                </Stack>
              </Inline>
            </Box>
          ))}
        </Stack>

        {children && <Flex flexDirection="column">{children}</Flex>}
      </Stack>
    </ResponsiveCard>
  );
};
